import { User, Company, Role } from '@adsk/alloy-provider';
import {
  OxygenRole,
  OxygenCompany,
  OxygenUser,
  AssigneeType,
  Assignee,
} from '../sdk/types/common/system';
import type { Oxygen } from '../providers';
import { HQUserStatus } from '../constants';
import { getAssigneeFromOxygenEntity } from './assigned-to-helpers';

type BuildOxygenObjects = (args: { users: User[]; companies: Company[]; roles: Role[] }) => {
  users: Record<OxygenUser['autodeskId'], OxygenUser>;
  companies: Record<OxygenCompany['id'], OxygenCompany>;
  roles: Record<OxygenRole['id'], OxygenRole>;
};

const buildOxygenObjects: BuildOxygenObjects = ({ users, companies = [], roles }) => {
  // We only handle used roles (used = a user in the project is assigned to them)
  const rolesCount: Record<string, number> = {};
  users.forEach(({ roleIds }) => {
    roleIds.forEach((roleId) => {
      rolesCount[roleId] = (rolesCount[roleId] || 0) + 1;
    });
  });

  const roleIdToOxygenId: Record<Role['id'], Role['memberGroupId']> = {};
  const rolesResult: Record<Role['memberGroupId'], OxygenRole> = roles.reduce<
    ReturnType<BuildOxygenObjects>['roles']
  >((result, role) => {
    const { memberGroupId, ...restOfRole } = role;
    if (!memberGroupId) {
      return result;
    }

    // eslint-disable-next-line no-param-reassign
    result[memberGroupId] = {
      ...restOfRole,
      oxygenType: AssigneeType.Role,
      type: AssigneeType.Role,
      isInProject: !!rolesCount[role.id],
      memberCount: rolesCount[role.id] || 0,
      oxygenId: memberGroupId,
      id: memberGroupId,
      serverId: role.id,
    };
    roleIdToOxygenId[role.id] = memberGroupId;
    return result;
  }, {});

  const companyIdToCompanyName: Record<Company['id'], Company['name']> = {};
  const companyIdToOxygenId: Record<Company['id'], Company['memberGroupId']> = {};
  const companiesResult: Record<Company['memberGroupId'], OxygenCompany> = companies.reduce<
    ReturnType<BuildOxygenObjects>['companies']
  >((result, company) => {
    const { memberGroupId, ...restOfCompany } = company;
    if (!memberGroupId) {
      return result;
    }

    // eslint-disable-next-line no-param-reassign
    result[memberGroupId] = {
      ...restOfCompany,
      oxygenType: AssigneeType.Company,
      type: AssigneeType.Company,
      oxygenId: memberGroupId,
      id: memberGroupId,
      serverId: company.id,
    };
    companyIdToCompanyName[company.id] = company.name;
    companyIdToOxygenId[company.id] = memberGroupId;
    return result;
  }, {});

  const usersResult = users.reduce<ReturnType<BuildOxygenObjects>['users']>((result, user) => {
    const { roleIds, name, ...restOfUser } = user;
    if (!user.autodeskId) {
      return result;
    }

    // eslint-disable-next-line no-param-reassign
    result[user.autodeskId] = {
      ...restOfUser,
      oxygenType: AssigneeType.User,
      type: AssigneeType.User,
      dbId: user.id,
      id: user.autodeskId,
      oxygenId: user.autodeskId,
      company: {
        id: user.companyId,
        oxygenId: companyIdToOxygenId[user.companyId],
        name: companyIdToCompanyName[user.companyId],
      },
      roles: user.roleIds.map((roleId) => ({
        id: roleIdToOxygenId[roleId],
        oxygenId: roleIdToOxygenId[roleId],
      })),
      rolesMap: roleIds,
      profileImg50: user.imageUrl,
      name: name || user.email,
    };

    return result;
  }, {});

  return {
    users: usersResult,
    companies: companiesResult,
    roles: rolesResult,
  };
};

/** This function returns assignees for the project:
 * 1. All users in the project including deleted ones
 * 2. All companies in the project that contain members
 * 3. All roles in the project that contain members
 */
export const getAllProjectAssignees = (oxygen: Oxygen): Assignee[] => {
  const { users, companies, roles } = oxygen;

  return [
    ...Object.values(users),
    ...Object.values(companies).filter(({ memberCount }) => memberCount > 0),
    ...Object.values(roles).filter(({ memberCount }) => memberCount > 0),
  ].map(getAssigneeFromOxygenEntity);
};

export const getFilterValidAssignees =
  (oxygen: Oxygen) =>
  (assignees?: Assignee[] | null): Assignee[] => {
    const { users, roles, companies } = oxygen;
    return (
      assignees?.filter((assignee) => {
        const { id, type } = assignee;

        switch (type) {
          case AssigneeType.User:
            return id in users && users[id].status !== HQUserStatus.DELETED;
          case AssigneeType.Role:
            return id in roles;
          case AssigneeType.Company:
            return id in companies;
          default:
            return false;
        }
      }) || []
    );
  };
export default buildOxygenObjects;
